import {
  getFormattedAddress,
  LocationDTO,
  MeasureDTO,
} from '@sales-center/common'
import { GridSize } from '@mui/material/Grid'
import { MEASURE_DISPLAY_MAP } from './measureDisplayMap'

export type MeasureVariant = 'detail' | 'card' | 'location' | 'mapView'

export const variantWidth: Record<MeasureVariant, string> = {
  detail: '355px',
  card: '591px',
  location: '100%',
  mapView: '100%',
}

export const variantHeight: Record<MeasureVariant, string> = {
  detail: '400px',
  card: '291px',
  location: '100%',
  mapView: '100%',
}

export const getGridSizes = (
  variant: MeasureVariant,
  xs: GridSize,
  md?: GridSize,
): { xs: GridSize; md?: GridSize } => {
  return variant === 'card' ? { xs } : { xs, md }
}

export function hasValidAddress(locations: LocationDTO[]): boolean {
  if (!locations || locations.length === 0) {
    return false
  }

  return locations.some(
    (l) => l.street1 && l.city && l.stateAbbreviation && l.postalCode,
  )
}

const defaultLocation: LocationDTO = {
  id: 0,
  billingLocation: false,
  contactId: 0,
  salesTeamId: 0,
  branchId: null,
  branch: null,
  locationType: null,
  locationTypeId: null,
  divisionId: null,
  division: null,
  firstName: null,
  lastName: null,
  title: null,
  company: null,
  emailAddresses: [],
  phoneNumber: null,
  phoneExt: null,
  altPhoneNumber: null,
  altPhoneExt: null,
  street1: null,
  street2: null,
  city: null,
  stateAbbreviation: null,
  postalCode: null,
  county: null,
  country: null,
  longitude: null,
  latitude: null,
  externalLinkId: null,
  externalLinkType: 'Location',
  locationCode: null,
  taxCode: null,
  isSchedulable: false,
  mobilePhone: null,
  draftAgreementsUpdated: false,
  propertyDetails: undefined,
}

export function transformMeasureToLocation(measure: MeasureDTO): LocationDTO {
  return {
    ...defaultLocation,
    propertyDetails: measure,
  }
}

export function getFormattedAddressFromLocation(location: LocationDTO): string {
  return getFormattedAddress(
    location.street1 || '',
    location.street2 || '',
    location.city || '',
    location.stateAbbreviation || '',
    location.postalCode || '',
  )
}

type DisplayRecord = Record<string, string | number | null>

export type Detail = { label: string; value: string | number | null }

export type DisplayDetails = Detail[]

function getNestedValue(obj: any, path: string[] | string) {
  if (!obj || !path) return null

  const keys = Array.isArray(path) ? path : [path]

  let current = obj

  for (const key of keys) {
    if (current == null) return null
    current = current[key as keyof typeof current]
  }

  return current ?? null
}

export function flattenMeasureForDisplay(measure: MeasureDTO): DisplayDetails {
  const result: DisplayRecord = {}

  Object.entries(MEASURE_DISPLAY_MAP).forEach(([sectionKey, fields]) => {
    const sectionObj = (measure as any)[sectionKey]

    if (!sectionObj) return

    if (Array.isArray(sectionObj)) {
      // handle arrays like buildings
      sectionObj.forEach((item, _) => {
        fields.forEach((f) => {
          const raw = getNestedValue(item, f.path)
          const value = f.formatter ? f.formatter(raw, item) : raw
          // Add index to label so each building is unique
          result[f.label] = value ?? null
        })
      })
    } else {
      // handle single object sections
      fields.forEach((f) => {
        const raw = getNestedValue(sectionObj, f.path)
        const value = f.formatter ? f.formatter(raw, sectionObj) : raw
        result[f.label] = value ?? null
      })
    }
  })

  const displayDetails = Object.entries(result).map(([key, value]) => {
    return { label: key, value: value }
  })

  return displayDetails
}
